import { formatTraffic, formatPing } from "./formatters";

import OpenDns from "../../../../assets/imgs/imagesCard/openDnsLogo.png";
import Globo from "../../../../assets/imgs/imagesCard/globoLogo.png";
import Ntp from "../../../../assets/imgs/imagesCard/ftntp-client.svg";
import CloudFlare from "../../../../assets/imgs/imagesCard/cloudLogo.png";
import google from "../../../../assets/imgs/imagesCard/googleLogo.png";

const DESTINO_IMAGE_MAP = {
  google: google,
  "open dns": OpenDns,
  cloudflare: CloudFlare,
  ntp: Ntp,
  globo: Globo,
};

function getDestinoImage(description) {
  const key = description?.toLowerCase()?.trim();
  return DESTINO_IMAGE_MAP[key] || null;
}

/**
 * Cria os fetchers de dados do overview já amarrados às dependências do
 * componente (api/filterData do contexto, setters de estado e alguns estados
 * como selectedOnu/oltHosts/onu/loadingPower). Chame no corpo do componente a
 * cada render — as funções capturam os valores atuais, igual a quando eram
 * definidas inline. Retorna só o que o componente consome; getOnuPowerRx/Tx
 * ficam internos (usados apenas por getOnuByService).
 */
export function createOverviewFetchers(deps) {
  const {
    api,
    filterData,
    selectedOnu,
    oltHosts,
    onu,
    loadingPower,
    setLoadingDashOverview,
    setTrafficInput,
    setTrafficOutput,
    setTrafficNow,
    setPacketLoss,
    setLatency,
    setSessoesAtivas,
    setErrorPacketCount,
    setSpeedNow,
    setStatusInterface,
    setRows,
    setPingDestinos,
    setPingHabilitado,
    setPingHistoryData,
    setPingHistoryLoading,
    setOnu,
    setSelectedOnu,
    setOltHosts,
    setOnuPower,
  } = deps;

  // chama /zabbix/serieTemporal injetando os identificadores de schema comuns
  // do overview (fabricante/funcao/modelo/dash); `body` sobrescreve se precisar
  const fetchSerieTemporal = (body, signal) =>
    api.post(
      "/zabbix/serieTemporal",
      {
        fabricante: filterData?.fabricante,
        funcao: filterData?.funcao,
        modelo: filterData?.modelo,
        dash: "overview",
        ...body,
      },
      { signal },
    );

  // corpo base do host/interface atual; `extra` completa/sobrescreve os campos
  const baseBody = (extra) => ({
    device: filterData?.device,
    interface: filterData?.interfaceId,
    hostids: filterData?.hostId,
    ...extra,
  });

  // Fetchers homogêneos do overview: mesma casca (loading → post → onData →
  // reset), variando só o corpo de /zabbix/serieTemporal e o estado consumido.
  // Casos com lógica própria (statusInterface, ping) seguem como funções à parte.
  const OVERVIEW_FETCHERS = [
    // séries temporais (gráficos) — consomem response.data direto
    {
      loadingKey: "traffiIn",
      body: (d, f) => baseBody({ nameFilter: "trafficIn", initial_date: d, final_date: f }),
      onData: (data) => setTrafficInput(data),
    },
    {
      loadingKey: "trafficOut",
      body: (d, f) => baseBody({ nameFilter: "trafficOut", initial_date: d, final_date: f }),
      onData: (data) => setTrafficOutput(data),
    },
    {
      loadingKey: "packetLoss",
      body: (d, f) => baseBody({ nameFilter: "packetLoss", initial_date: d, final_date: f, exactMatch: true, fillGap: 0 }),
      onData: (data) => setPacketLoss(data),
    },
    {
      loadingKey: "latency",
      body: (d, f) => baseBody({ nameFilter: "latency", initial_date: d, final_date: f, exactMatch: true }),
      onData: (data) => setLatency(data),
    },
    {
      loadingKey: "sessoesAtivas",
      // sessões são por host, não por interface — não envia `interface`
      body: (d, f) => ({ device: filterData?.device, hostids: filterData?.hostId, nameFilter: "sessoesAtivas", initial_date: d, final_date: f }),
      onData: (data) => setSessoesAtivas(data),
    },
    // valores instantâneos (lastItem) — consomem response.data.value
    {
      body: () => baseBody({ nameFilter: "trafficIn", lastItem: true }),
      onData: (data) => setTrafficNow((prev) => ({ ...prev, in: formatTraffic(data?.value || 0) })),
    },
    {
      body: () => baseBody({ nameFilter: "trafficOut", lastItem: true }),
      onData: (data) => setTrafficNow((prev) => ({ ...prev, out: formatTraffic(data?.value || 0) })),
    },
    {
      loadingKey: "erroPacketIn",
      body: () => baseBody({ nameFilter: "erroPacketIn", lastItem: true }),
      onData: (data) => setErrorPacketCount((prev) => ({ ...prev, in: data?.value || 0 })),
    },
    {
      loadingKey: "erroPacketOut",
      body: () => baseBody({ nameFilter: "erroPacketOut", lastItem: true }),
      onData: (data) => setErrorPacketCount((prev) => ({ ...prev, out: data?.value || 0 })),
    },
    {
      loadingKey: "speed",
      body: () => baseBody({ nameFilter: "ifSpeed", lastItem: true }),
      onData: (data) => setSpeedNow(formatTraffic(data?.value || 0)),
    },
  ];

  const runOverviewFetch = async (
    { loadingKey, body, onData },
    dateInitial,
    finalDate,
    signal,
  ) => {
    if (loadingKey) {
      setLoadingDashOverview((prev) => ({ ...prev, [loadingKey]: true }));
    }
    try {
      const response = await fetchSerieTemporal(
        body(dateInitial, finalDate),
        signal,
      );
      onData(response?.data);
    } catch (error) {
      console.error(error);
    } finally {
      if (loadingKey && !signal?.aborted) {
        setLoadingDashOverview((prev) => ({ ...prev, [loadingKey]: false }));
      }
    }
  };
  const getStatusInterface = async (dateInitial, finalDate, signal) => {
    setLoadingDashOverview((prev) => ({ ...prev, statusInterface: true }));
    try {
      const [response, itemsResponse] = await Promise.all([
        fetchSerieTemporal(
          {
            interface: filterData?.interfaceId,
            hostids: filterData?.hostId,
            nameFilter: "statusInterface",
            device: filterData?.device,
            lastItem: true,
          },
          signal,
        ),
        api.post(
          "/zabbix/items/list",
          {
            hostid: filterData?.hostId,
            searchItemName: `Status da Interface ${filterData?.interfaceId}*`,
          },
          { signal },
        ),
      ]);

      const items = itemsResponse?.data?.items;
      if (!items || items.length === 0) {
        setStatusInterface(null);
      } else {
        setStatusInterface(
          response?.data?.value === 1 || response?.data?.value === "1"
            ? true
            : false,
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (!signal.aborted) {
        setLoadingDashOverview((prev) => ({ ...prev, statusInterface: false }));
      }
    }
  };
  const getPing = async (dateInitial, finalDate, signal) => {
    setLoadingDashOverview((prev) => ({ ...prev, ping: true }));

    try {
      // --- 1. Busca as macros do host ---
      const macrosResponse = await api.post(
        "/zabbix/hosts/macros",
        { hostId: filterData?.hostId },
        { signal },
      );

      const macros = macrosResponse?.data?.macros || [];

      // --- 2. Verifica se o ping está habilitado ---
      const pingHabilitado = macros?.find(
        (m) => m?.macro === "{$PING_HABILITADO}",
      );
      const isEnabled = pingHabilitado?.value === "1";
      setPingHabilitado(isEnabled);

      if (!isEnabled) {
        setPingDestinos([]);
        return;
      }

      // --- 3. Extrai apenas os macros de destino e monta índice IP → macro ---
      const destinoMacros = macros?.filter((m) =>
        m?.macro?.includes("DESTINO"),
      );

      // Map de IP/host (valor da macro) → macro, para lookup O(1) adiante
      const macroByIp = new Map(
        destinoMacros?.map((m) => [m?.value?.trim(), m]),
      );

      // --- 4. Busca latência (último valor) e perda de pacotes (média) em paralelo ---
      const [latencyResponse, packetLossResponse] = await Promise.all([
        fetchSerieTemporal(
          {
            interface: filterData?.interfaceId,
            hostids: filterData?.hostId,
            itemsKeys: "latencia",
            lastItem: true,
          },
          signal,
        ),
        fetchSerieTemporal(
          {
            initial_date: dateInitial,
            final_date: finalDate,
            interface: filterData?.interfaceId,
            hostids: filterData?.hostId,
            itemsKeys: "perdaDePacotes",
            mean: true,
          },
          signal,
        ),
      ]);

      let latencyData = [];
      const rawLatency = latencyResponse?.data;
      if (rawLatency) {
        if (Array.isArray(rawLatency)) {
          latencyData = rawLatency;
        } else if (typeof rawLatency === "object") {
          if ("itemid" in rawLatency) {
            latencyData = [rawLatency];
          } else {
            latencyData = Object.values(rawLatency).filter(
              (val) => val && typeof val === "object" && "itemid" in val,
            );
          }
        }
      }

      // --- 5. Constrói índice IP → dados de latência a partir do nome ---
      const latencyByIp = new Map();

      for (const item of latencyData) {
        const match = item?.name?.match(/\b(?:\d{1,3}\.){3}\d{1,3}\b/);
        const extractedIp = match ? match[0] : null;
        if (extractedIp) {
          latencyByIp?.set(extractedIp, { key: extractedIp, item });
        }
      }

      let packetLossData = [];
      const rawPacketLoss = packetLossResponse?.data;
      if (rawPacketLoss !== undefined && rawPacketLoss !== null) {
        if (Array.isArray(rawPacketLoss)) {
          packetLossData = rawPacketLoss;
        } else if (typeof rawPacketLoss === "object") {
          packetLossData = Object.entries(rawPacketLoss).map(
            ([key, value]) => ({
              name: key,
              value: Number(value),
            }),
          );
        } else {
          const firstMacroIp = destinoMacros?.[0]?.value?.trim();
          const plName =
            latencyData[0]?.name ||
            (firstMacroIp ? `perdaDePacotes[${firstMacroIp}]` : "");
          packetLossData = [
            {
              name: plName,
              value: Number(rawPacketLoss),
            },
          ];
        }
      }

      // --- 6. Busca ícones customizados e faz download das imagens em paralelo ---
      let iconsWithImages = [];
      try {
        const iconsResponse = await api.post(
          "/anexos/findMany",
          { type: "ping_icon" },
          { signal },
        );
        const pingIcons = iconsResponse?.data || [];

        iconsWithImages = await Promise.all(
          pingIcons.map(async (icon) => {
            try {
              const imgResponse = await api.post(
                "/anexos/download",
                { id: icon?.id },
                { responseType: "arraybuffer", signal },
              );
              const blob = new Blob([imgResponse?.data], {
                type: icon?.mimeType,
              });
              return { ...icon, imageUrl: URL.createObjectURL(blob) };
            } catch {
              return { ...icon, imageUrl: null };
            }
          }),
        );
      } catch (err) {
        console.error("Erro ao buscar ícones de ping:", err);
      }

      // Índice de ícones por descrição (lowercase, sem espaços extras) → O(1)
      const iconByDescription = new Map(
        iconsWithImages?.map((icon) => [
          icon?.descricao?.toLowerCase()?.trim(),
          icon,
        ]),
      );

      // --- 7. Resolve entrada de latência para um IP ou domínio ---
      // Ordem de prioridade (da mais confiável para a menos):
      const resolveLatencyEntry = (ip) => {
        // Tentativa 1: correspondência exata pelo IP extraído
        if (latencyByIp?.has(ip)) return latencyByIp?.get(ip);

        // Tentativa 2: o valor da macro aparece dentro do campo "name" do item
        // Cobre casos onde o Zabbix registra o domínio/IP no nome descritivo
        for (const item of latencyData) {
          if (item?.name?.toLowerCase()?.includes(ip?.toLowerCase())) {
            const match = item?.name?.match(/\\b(?:\\d{1,3}\\.){3}\\d{1,3}\\b/);
            return { key: match ? match[0] : null, item };
          }
        }

        // Tentativa 3: o IP extraído do nome não pertence a nenhuma outra macro
        // Cobre casos onde o Zabbix resolve o domínio para um IP diferente do configurado
        for (const [, entry] of latencyByIp) {
          const keyIp = entry?.key;
          if (keyIp && !macroByIp?.has(keyIp)) {
            return entry;
          }
        }

        return null;
      };

      // --- 8. Mapeia cada macro de destino com seus dados consolidados ---
      const destinos = destinoMacros?.map((macro) => {
        const ip = macro?.value?.trim();
        const descKey = macro?.description?.toLowerCase()?.trim();

        // Resolve latência e perda de pacotes
        const latencyEntry = resolveLatencyEntry(ip);
        const resolvedIp = latencyEntry?.key;

        const ping = formatPing(latencyEntry?.item?.value || 0);

        let packetLoss = 0;
        const plItem = packetLossData.find((pl) => {
          if (resolvedIp && pl?.name?.includes(resolvedIp)) return true;
          if (pl?.name?.toLowerCase()?.includes(ip?.toLowerCase())) return true;
          return false;
        });
        if (plItem) {
          packetLoss = plItem.value;
        }

        // Resolve ícone: customizado (por descrição) → fallback estático
        const customIcon = iconByDescription?.get(descKey);
        const image =
          customIcon?.imageUrl || getDestinoImage(macro?.description);

        return {
          description: macro?.description,
          value: ip,
          macro: macro?.macro,
          image,
          pingData: { ping, packetLoss },
        };
      });

      // --- 9. Revoga os blobs dos ícones que nenhum destino usou ---
      // Esta função roda a cada auto-refresh (60s) e cria um blob novo por
      // ícone; os que não entram em `destinos` nunca chegariam ao revoke
      // abaixo e ficariam acumulando na memória do renderer.
      const imagensEmUso = new Set((destinos || []).map((d) => d?.image));
      iconsWithImages.forEach((icon) => {
        if (icon?.imageUrl && !imagensEmUso.has(icon.imageUrl)) {
          URL.revokeObjectURL(icon.imageUrl);
        }
      });

      // --- 10. Atualiza estado revogando URLs de blob anteriores para evitar leak ---
      setPingDestinos((prevDestinos) => {
        prevDestinos.forEach((destino) => {
          if (destino?.image?.startsWith?.("blob:")) {
            URL.revokeObjectURL(destino?.image);
          }
        });
        return destinos;
      });
    } catch (error) {
      console.error(error);
    } finally {
      if (!signal?.aborted) {
        setLoadingDashOverview((prev) => ({ ...prev, ping: false }));
      }
    }
  };

  // busca série temporal de latência e perda de pacotes do destino clicado
  // overrideDates permite sobrescrever o range (ex: ao fazer zoom no modal)
  const getPingHistory = async (destino, signal, dateInitial, finalDate) => {
    if (!destino?.value) return;

    const ip = destino?.value;

    setPingHistoryLoading({ latency: true, packetLoss: true });
    try {
      const [latencyRes, packetLossRes] = await Promise.all([
        fetchSerieTemporal(
          {
            hostids: filterData?.hostId,
            itemsKeys: `latencia[${ip}]`,
            exactMatch: true,
            initial_date: dateInitial,
            final_date: finalDate,
          },
          signal,
        ),
        fetchSerieTemporal(
          {
            hostids: filterData?.hostId,
            itemsKeys: `perdaDePacotes[${ip}]`,
            exactMatch: true,
            fillGap: 0,
            initial_date: dateInitial,
            final_date: finalDate,
          },
          signal,
        ),
      ]);

      if (!signal?.aborted) {
        setPingHistoryData({
          latency: Array.isArray(latencyRes?.data) ? latencyRes?.data : [],
          packetLoss: Array.isArray(packetLossRes?.data)
            ? packetLossRes?.data
            : [],
        });
      }
    } catch (error) {
      if (!signal?.aborted) console.error(error);
    } finally {
      if (!signal?.aborted) {
        setPingHistoryLoading({ latency: false, packetLoss: false });
      }
    }
  };

  const getOnuByService = async (signal, dateInitial, finalDate) => {
    try {
      if (onu?.servicoId === filterData?.serviceId && oltHosts?.length > 0) {
        getOnuPowerRx(signal, selectedOnu, dateInitial, finalDate);
        getOnuPowerTx(signal, selectedOnu, dateInitial, finalDate);
        return;
      }

      const OLTs = await api.post(
        "/zabbix/hostList",
        {
          groupNames: "OLT",
        },
        { signal },
      );

      setOltHosts(OLTs?.data || []);

      const response = await api.post(
        "/interno/findMany/hostsCliente",
        {
          clienteId: filterData?.clienteId,
          servicoId: filterData?.serviceId,
          onlyOnu: true,
        },
        { signal },
      );

      if (response?.data?.length > 0) {
        const onuList = response.data;
        setOnu({ servicoId: filterData?.serviceId, onu: onuList });

        // Seleciona a primeira ONU por padrão
        setSelectedOnu(onuList[0]);
      } else {
        setOnu({ servicoId: filterData?.serviceId, onu: [] });
        setSelectedOnu(null);
      }
    } catch (error) {
      console.error(error);
      setSelectedOnu(null);
    }
  };

  const getOnuPowerRx = async (signal, selectedOnu, dateInitial, finalDate) => {
    try {
      loadingPower.rx = true;

      const host = oltHosts?.find((host) =>
        host?.host?.includes(selectedOnu?.ipOlt),
      );
      const onuItem = selectedOnu?.onuItem?.split(" - ");

      const response = await api.post(
        "/zabbix/serieTemporal",
        {
          hostids: host?.hostid,
          device: "onu",
          serialOnu: onuItem?.[1]?.trim(),
          usernameCliente: onuItem?.[2]?.trim(),
          posicaoOnu: onuItem?.[0]?.trim(),
          initial_date: dateInitial,
          final_date: finalDate,
          nameFilter: "receivedPower",
          fabricante: selectedOnu?.fabricante,
          funcao: selectedOnu?.funcao,
          modelo: selectedOnu?.modelo,
          dash: "onu",
        },
        { signal },
      );

      setOnuPower((prev) => ({ ...prev, rx: response?.data || [] }));
    } catch (error) {
      setOnuPower((prev) => ({ ...prev, rx: [] }));
      console.error(error);
    } finally {
      loadingPower.rx = false;
    }
  };

  const getOnuPowerTx = async (signal, selectedOnu, dateInitial, finalDate) => {
    try {
      loadingPower.tx = true;
      const host = oltHosts?.find((host) =>
        host?.host?.includes(selectedOnu?.ipOlt),
      );
      const onuItem = selectedOnu?.onuItem?.split(" - ");
      const response = await api.post(
        "/zabbix/serieTemporal",
        {
          hostids: host?.hostid,
          device: "onu",
          serialOnu: onuItem?.[1]?.trim(),
          usernameCliente: onuItem?.[2]?.trim(),
          posicaoOnu: onuItem?.[0]?.trim(),
          initial_date: dateInitial,
          final_date: finalDate,
          nameFilter: "transmittedPower",
          fabricante: selectedOnu?.fabricante,
          funcao: selectedOnu?.funcao,
          modelo: selectedOnu?.modelo,
          dash: "onu",
        },
        { signal },
      );

      setOnuPower((prev) => ({ ...prev, tx: response?.data || [] }));
    } catch (error) {
      setOnuPower((prev) => ({ ...prev, tx: [] }));
      console.error(error);
    } finally {
      loadingPower.tx = false;
    }
  };

  const getAlerts = async (dateInitial, finalDate, signal) => {
    setLoadingDashOverview((prev) => ({ ...prev, alerts: true }));

    try {
      const response = await api.post(
        "/zabbix/items/alerts",
        {
          device: filterData?.device,
          initial_date: dateInitial,
          final_date: finalDate,
          interface: filterData?.interfaceId,
          hostids: filterData?.hostId,
          // Necessários para restringir os alertas às interfaces do cliente
          // quando o host está no provedor (tag locatedAt = PROVEDOR)
          clienteId: filterData?.clienteId,
          servicoId: filterData?.serviceId,
        },
        { signal },
      );

      setRows(
        response?.data?.map((item, index) => {
          return {
            ...item,
            id: index,
          };
        }) || [],
      );
    } catch (error) {
      console.error(error);
    } finally {
      if (!signal.aborted) {
        setLoadingDashOverview((prev) => ({ ...prev, alerts: false }));
      }
    }
  };

  return {
    OVERVIEW_FETCHERS,
    runOverviewFetch,
    getStatusInterface,
    getPing,
    getPingHistory,
    getAlerts,
    getOnuByService,
  };
}
